import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useProfile } from "@/contexts/ProfileContext";
import { logPlatformEvent } from "@/hooks/usePlatformEvents";
import { queryKeys } from "@/lib/query-keys";

export interface Review {
  id: string;
  rating: number;
  title: string | null;
  content: string | null;
  created_at: string;
  updated_at: string;
  /** Public reply from the asset owner (migration 20260801060000). */
  seller_response: string | null;
  seller_responded_at: string | null;
  is_verified_purchase?: boolean;
  user: {
    id: string;
    user_id: string | null;
    display_name: string | null;
    avatar_url: string | null;
    username: string | null;
    is_seller: boolean | null;
    seller_verified: boolean | null;
    is_early_founder: boolean | null;
  };
}

export function useAssetReviews(assetId: string) {
  return useQuery({
    queryKey: queryKeys.assets.reviews(assetId),
    queryFn: async () => {
      const { data, error } = await supabase
        .from("reviews")
        .select(`
          id,
          rating,
          title,
          content,
          created_at,
          updated_at,
          seller_response,
          seller_responded_at,
          user:public_profiles!reviews_user_id_fkey (
            id,
            user_id,
            display_name,
            avatar_url,
            username,
            is_seller,
            seller_verified,
            is_early_founder
          )
        `)
        .eq("asset_id", assetId)
        .order("created_at", { ascending: false });

      if (error) throw error;

      // "Verified" = the reviewer holds the asset (a purchase or free claim
      // that wasn't refunded). Purchases are private to their buyer, so the
      // browser can't check this itself — asset_verified_reviewers answers it
      // for this listing's approved reviewers, for every visitor.
      const verifiedSet = new Set<string>();
      if ((data ?? []).length > 0) {
        const { data: verified, error: verifiedError } = await supabase.rpc("asset_verified_reviewers", {
          p_asset_id: assetId,
        });
        if (verifiedError) throw verifiedError;
        (verified ?? []).forEach((v) => verifiedSet.add(v.profile_id));
      }

      return (data as Review[]).map((review) => {
        const userId =
          review.user && !Array.isArray(review.user) ? review.user.id : undefined;
        return {
          ...review,
          is_verified_purchase: userId ? verifiedSet.has(userId) : false,
        };
      });
    },
    enabled: !!assetId,
    staleTime: 5 * 60_000,
    gcTime: 30 * 60_000,
  });
}

export function useCreateReview() {
  const queryClient = useQueryClient();
  const { profile: currentProfile } = useProfile();

  return useMutation({
    mutationFn: async ({
      assetId,
      rating,
      title,
      content,
    }: {
      assetId: string;
      rating: number;
      title?: string;
      content?: string;
    }) => {
      // Get current user's profile
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const { data: profile } = await supabase
        .from("profiles")
        .select("id")
        .eq("user_id", user.id)
        .maybeSingle();

      if (!profile) throw new Error("Profile not found");

      const { data, error } = await supabase
        .from("reviews")
        .insert({
          asset_id: assetId,
          user_id: profile.id,
          rating,
          title,
          content,
        })
        .select()
        .single();

      if (error) throw error;

      const { data: asset } = await supabase
        .from("assets")
        .select("seller_id, slug")
        .eq("id", assetId)
        .maybeSingle();

      return { ...data, seller_id: asset?.seller_id ?? null, slug: asset?.slug ?? null };
    },
    onMutate: async (variables) => {
      if (!currentProfile?.id) return { previous: undefined };
      const key = queryKeys.assets.reviews(variables.assetId);
      await queryClient.cancelQueries({ queryKey: key });
      const previous = queryClient.getQueryData<Review[]>(key);

      const optimisticReview: Review = {
        id: `optimistic-${Date.now()}`,
        rating: variables.rating,
        title: variables.title ?? null,
        content: variables.content ?? null,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        seller_response: null,
        seller_responded_at: null,
        is_verified_purchase: true,
        user: {
          id: currentProfile.id,
          user_id: currentProfile.user_id ?? null,
          display_name: currentProfile.display_name ?? null,
          avatar_url: currentProfile.avatar_url ?? null,
          username: currentProfile.username ?? null,
          is_seller: currentProfile.is_seller ?? null,
          seller_verified: (currentProfile as { seller_verified?: boolean | null }).seller_verified ?? null,
          is_early_founder: (currentProfile as { is_early_founder?: boolean | null }).is_early_founder ?? null,
        },
      };

      queryClient.setQueryData<Review[]>(key, (old) => [optimisticReview, ...(old ?? [])]);
      return { previous };
    },
    onError: (_err, variables, ctx) => {
      if (ctx?.previous !== undefined) {
        queryClient.setQueryData(queryKeys.assets.reviews(variables.assetId), ctx.previous);
      }
    },
    onSuccess: (data, variables) => {
      if (data.seller_id && data.seller_id !== data.user_id) {
        logPlatformEvent({
          event_type: "review_submitted",
          actor_profile_id: data.user_id,
          target_profile_id: data.seller_id,
          target_asset_id: variables.assetId,
          metadata: {
            rating: variables.rating,
            title: variables.title || null,
            has_content: !!variables.content,
            slug: data.slug,
          },
        });
      }
    },
    onSettled: (_data, _err, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.assets.reviews(variables.assetId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.assets.detailAll });
    },
  });
}

/**
 * Asset owner's public reply to a review.
 *
 * The DB guard (guard_review_seller_response) allows the owner to touch only
 * the response fields, and stamps `seller_responded_at` server-side. Pass an
 * empty string to withdraw a reply. A separate trigger notifies the reviewer.
 */
export function useRespondToReview() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      reviewId,
      response,
    }: {
      reviewId: string;
      assetId: string;
      response: string;
    }) => {
      const trimmed = response.trim();
      // `.select()` so an RLS-filtered no-op surfaces as an error rather than a
      // success toast over a reply that was never saved.
      const { data, error } = await supabase
        .from("reviews")
        .update({ seller_response: trimmed || null })
        .eq("id", reviewId)
        .select("id, seller_response, seller_responded_at");
      if (error) throw error;
      if (!data || data.length === 0) {
        throw new Error("Only the asset owner can respond to this review.");
      }
      return data[0];
    },
    onSettled: (_data, _err, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.assets.reviews(variables.assetId) });
    },
  });
}
